import {functionOf, TListOfElement, TText} from '@packages/systems/wfdl-ast';
import {withSystem, EvalEnv, EnvTypes, type Type} from '@packages/systems/wfdl';
import {TOption} from '@packages/systems/plugins/Option';
import {type DynamoEnvValueType} from './types';
import {
  type CurrencySettings,
  renderPriceFromSettings,
} from '@packages/systems/core/utils/CurrencyUtils';
import {memoize} from '@packages/utilities/memo';
import {createAddDynamoUtilsToEnv} from '@packages/systems/dynamo/wfdl';
import {TCaseSensitiveText} from '@packages/systems/plugins/Text';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {
  BooleanTag,
  FALSE,
  toBooleanTag,
  TPredicate,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import {TDynamoPrice} from '@packages/systems/plugins-utils/DynamoGateway';

/**
 * Converts text values to lowercase based on case sensitivity setting.
 * This utility function handles the common pattern of conditionally converting
 * text to lowercase for case-insensitive comparisons in Dynamo field operators.
 *
 * @param caseSensitive - Boolean tag indicating whether comparison should be case-sensitive
 * @param searchText - The search/comparison text to potentially convert
 * @param targetText - The target text to potentially convert
 * @returns Object containing the potentially lowercased search and target text values
 *
 * @example
 * ```typescript
 * // Case-insensitive comparison
 * const result = convertTextForCaseComparison(
 *   { tag: 'False', value: {} },
 *   'HELLO',
 *   'hello world'
 * );
 * // result: { searchText: 'hello', targetText: 'hello world' }
 *
 * // Case-sensitive comparison
 * const result = convertTextForCaseComparison(
 *   { tag: 'True', value: {} },
 *   'HELLO',
 *   'hello world'
 * );
 * // result: { searchText: 'HELLO', targetText: 'hello world' }
 * ```
 */
export function convertTextForCaseComparison(
  caseSensitive: BooleanTag,
  searchText: string,
  targetText: string
): {searchText: string; targetText: string} {
  if (caseSensitive.tag === 'False') {
    return {
      searchText: searchText.toLowerCase(),
      targetText: targetText.toLowerCase(),
    };
  }
  return {
    searchText,
    targetText,
  };
}

type CaseSensitiveText = {caseSensitive: BooleanTag; text: string};

/**
 * Generic factory function that creates text-based operator functions for Dynamo field types.
 * This eliminates code duplication across PlainText, Phone, Email, and other text-based field types.
 *
 * @param fieldType - The Dynamo field type constructor (e.g., TDynamoPlainText, TDynamoPhone, TDynamoEmail)
 * @returns Object containing all standard text-based operator functions
 *
 * @example
 * ```typescript
 * import {TDynamoPhone} from './Phone';
 * const phoneOperators = createDynamoTextOperators(TDynamoPhone);
 * export const dynamoPhoneEquals = phoneOperators.equals;
 * export const dynamoPhoneContains = phoneOperators.contains;
 * // ... etc
 * ```
 */
export function createDynamoTextOperators(fieldType: Type) {
  const equals: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'is'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return FALSE;
        }

        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          text,
          dynamoValue.val
        );
        return toBooleanTag(searchText === targetText);
      },
  };

  const doesNotEqual: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'is not'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return TRUE;
        }

        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          text,
          dynamoValue.val
        );
        return toBooleanTag(searchText !== targetText);
      },
  };

  const contains: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'contains'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: search}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return FALSE;
        }

        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          search,
          dynamoValue.val
        );
        return toBooleanTag(targetText.indexOf(searchText) !== -1);
      },
  };

  const doesNotContain: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'does not contain'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: search}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return TRUE;
        }

        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          search,
          dynamoValue.val
        );
        return toBooleanTag(targetText.indexOf(searchText) === -1);
      },
  };

  const startsWith: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'starts with'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: target}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return FALSE;
        }
        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          target,
          dynamoValue.val
        );
        return toBooleanTag(
          targetText.slice(0, searchText.length) === searchText
        );
      },
  };

  const doesNotStartWith: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'does not start with'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: target}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string'
        ) {
          return TRUE;
        }

        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          target,
          dynamoValue.val
        );
        return toBooleanTag(
          targetText.slice(0, searchText.length) !== searchText
        );
      },
  };

  const endsWith: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'ends with'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: target}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string' ||
          target.length > dynamoValue.val.length
        ) {
          return FALSE;
        }

        const valueEnd = dynamoValue.val.slice(
          dynamoValue.val.length - target.length
        );
        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          target,
          valueEnd
        );

        return toBooleanTag(targetText === searchText);
      },
  };

  const doesNotEndWith: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'does not end with'},
        functionOf(
          withSystem({isBindable: true}, TCaseSensitiveText),
          TPredicate(TOption(fieldType))
        )
      ),
    },
    value:
      ({caseSensitive, text: target}: CaseSensitiveText) =>
      (dynamoValue: DynamoEnvValueType<unknown>) => {
        if (
          dynamoValue === null ||
          dynamoValue.tag === 'None' ||
          typeof dynamoValue.val !== 'string' ||
          target.length > dynamoValue.val.length
        ) {
          return TRUE;
        }

        const valueEnd = dynamoValue.val.slice(
          dynamoValue.val.length - target.length
        );
        const {searchText, targetText} = convertTextForCaseComparison(
          caseSensitive,
          target,
          valueEnd
        );

        return toBooleanTag(targetText !== searchText);
      },
  };

  const isSet: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem({label: 'is set'}, TPredicate(TOption(fieldType))),
    },
    value: (dynamoValue: DynamoEnvValueType<unknown>) => {
      if (
        dynamoValue === null ||
        dynamoValue.tag === 'None' ||
        typeof dynamoValue.val !== 'string'
      ) {
        return FALSE;
      }
      return TRUE;
    },
  };

  const isNotSet: EnvTypes.TypedValue = {
    envType: {
      exported: 'visible',
      dataType: withSystem(
        {label: 'is not set'},
        TPredicate(TOption(fieldType))
      ),
    },
    value: (dynamoValue: DynamoEnvValueType<unknown>) => {
      if (
        dynamoValue === null ||
        dynamoValue.tag === 'None' ||
        typeof dynamoValue.val !== 'string'
      ) {
        return TRUE;
      }
      return FALSE;
    },
  };

  const operandFunction = operand(TOption(fieldType));

  return {
    equals,
    doesNotEqual,
    contains,
    doesNotContain,
    startsWith,
    doesNotStartWith,
    endsWith,
    doesNotEndWith,
    isSet,
    isNotSet,
    operand: operandFunction,
  };
}

// GraphQL price type
// https://github.com/webflow/webflow/blob/dev/public/js/designer-flux/test/components/render/fixtures/introspectionResponse.js#L1147
type Price = {
  value: number;
  unit: string;
  decimalValue: number;
  string: string;
};

const _dynamoPriceToText = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoPrice)),
      TText
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value:
    (currencySettings: CurrencySettings | undefined) =>
    (x: DynamoEnvValueType<Price>) => {
      if (x === null || x.tag === 'None') {
        return '';
      }
      const formattedPrice = renderPriceFromSettings(
        {
          value: x.val.value,
          unit: x.val.unit,
        },
        currencySettings
      );
      return formattedPrice;
    },
} as const;

const _dynamoPriceToListOfElements = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoPrice)),
      TListOfElement
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value:
    (commerceSettings: CurrencySettings | undefined) =>
    (x: DynamoEnvValueType<Price>) => {
      if (x === null) {
        return [];
      }

      if (x.tag === 'Some') {
        const formattedPrice = renderPriceFromSettings(
          {
            value: x.val.value,
            unit: x.val.unit,
          },
          commerceSettings
        );
        return [
          {
            text: true,
            type: null,
            id: 'temp',
            children: [],
            data: {value: formattedPrice},
          },
        ];
      } else {
        return [];
      }
    },
} as const;

export const dynamoPriceToText: EnvTypes.TypedValue = _dynamoPriceToText;
export const dynamoPriceToListOfElements: EnvTypes.TypedValue =
  _dynamoPriceToListOfElements;

export const addDynamoUtilsToEnv = memoize(
  (
    env: EvalEnv.EvalEnvType,
    ecommCurrencySettings: CurrencySettings | undefined
  ) => {
    const dynamoPriceToListOfElementsWithCommerceSettings =
      _dynamoPriceToListOfElements.value(ecommCurrencySettings);
    const addDynamoPriceToTextWithCommerceSettings = _dynamoPriceToText.value(
      ecommCurrencySettings
    );
    return createAddDynamoUtilsToEnv({
      dynamoPriceToText: addDynamoPriceToTextWithCommerceSettings,
      dynamoPriceToListOfElements:
        dynamoPriceToListOfElementsWithCommerceSettings,
    })(env);
  }
);
